/**
 * Issue Reward Modal
 * Allows authorized users to create a voucher and send it to an individual
 *
 * Features:
 * - Two modes: "Select an Offer" (offer-linked voucher) or "Custom Reward" (manual)
 * - Offer mode: picks a pre-configured offer, generates offer-linked voucher
 * - Custom mode: Organization autocomplete, manual amount/dates (original flow)
 * - Optional location selection (only shown if org has locations with facility IDs)
 * - Recipient name/email (can create new contact)
 * - Option to email voucher to recipient
 */

import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import { getLocations } from "../../services/locations.service";
import type { Organization } from "../../types/organization.types";
import type { OrganizationCTA } from "../../types/organizationCTA.types";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { US_STATES } from "../../types/event.types";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface IssueRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (voucherCode: string) => void;
  onSuccessDismiss?: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialState?: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  membershipId: string;
  organizationId: string;
  locationId: string;
  voucherAmount: string;
  voucherName: string;
  startDate: string;
  endDate: string;
  sendEmail: boolean;
  issueReason: string;
}

interface OfferOption {
  id: string;
  name: string;
  value_amount: number | null;
  value_type: string | null;
  redemption_scope: string | null;
  allowed_organization_types?: string[] | null;
  valid_from: string | null;
  valid_to: string | null;
  voucher_source_description: string | null;
  geographic_scope: string | null;
  _offer_states?: string[];
  _redemption_organizations?: Array<{
    id: string;
    name: string;
    city?: string | null;
    state?: string | null;
  }>;
}

function formatOfferValidityDate(value: string): string {
  const legacyMidnightUtc = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?(?:Z|\+00:00)$/,
  );
  if (legacyMidnightUtc) {
    const [, year, month, day] = legacyMidnightUtc;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      12,
    ).toLocaleDateString();
  }
  return new Date(value).toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
  });
}

export default function IssueRewardModal({
  isOpen,
  onClose,
  onSuccess,
  onSuccessDismiss,
  initialFirstName = "",
  initialLastName = "",
  initialEmail = "",
  initialState = "",
}: IssueRewardModalProps) {
  const queryClient = useQueryClient();

  // Reward type toggle
  const [rewardType, setRewardType] = useState<"offer" | "custom">("offer");
  const [selectedOffer, setSelectedOffer] = useState<OfferOption | null>(null);
  const [selectedState, setSelectedState] = useState<string>(initialState);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    firstName: initialFirstName,
    lastName: initialLastName,
    email: initialEmail,
    membershipId: "",
    organizationId: "",
    locationId: "",
    voucherAmount: "20",
    voucherName: "Reward Voucher",
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    sendEmail: true,
    issueReason: "",
  });

  // UI state
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [createdVoucherCode, setCreatedVoucherCode] = useState("");
  const [step, setStep] = useState<"form" | "success">("form");
  const [isDuplicateVoucher, setIsDuplicateVoucher] = useState(false);
  const [alreadyRedeemed, setAlreadyRedeemed] = useState(false);
  // The PERSISTED voucher value returned by the server. The success screen previously showed the
  // amount from the FORM, so a replay of an existing voucher displayed the requested value rather
  // than the value the voucher actually carries.
  const [persistedAmount, setPersistedAmount] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [orgVoucherCtas, setOrgVoucherCtas] = useState<OrganizationCTA[]>([]);
  const [orgVoucherCta, setOrgVoucherCta] = useState<OrganizationCTA | null>(
    null,
  );
  const [selectedOrgVoucherCtaId, setSelectedOrgVoucherCtaId] = useState("");
  const [ctaValidationErrors, setCtaValidationErrors] = useState<string[]>([]);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // Close org dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(e.target as Node)
      ) {
        setShowOrgDropdown(false);
      }
    };
    if (showOrgDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showOrgDropdown]);

  // Fetch active offers for offer mode, enriched with linked org states
  const { data: activeOffers = [] } = useQuery({
    queryKey: ["active-offers-for-reward"],
    queryFn: async () => {
      const { data } = await supabase
        .from("offers")
        .select(
          `id, name, value_amount, value_type, redemption_scope, allowed_organization_types, valid_from, valid_to, voucher_source_description, geographic_scope,
           offer_states(state_code),
           offer_organizations(
             organizations(id, name, address_city, address_state, city_tag, state_tag)
           )`,
        )
        .eq("is_active", true)
        .eq("is_deleted", false)
        .order("name");

      return ((data as any[]) || []).map((offer) => {
        const statesList: string[] = [];
        if (offer.offer_states) {
          for (const os of offer.offer_states) {
            if (os.state_code && !statesList.includes(os.state_code)) {
              statesList.push(os.state_code);
            }
          }
        }
        const redemptionOrganizations =
          offer.offer_organizations
            ?.map((link: any) => link.organizations)
            .filter(Boolean)
            .map((org: any) => ({
              id: org.id,
              name: org.name,
              city: org.address_city || org.city_tag || null,
              state: org.address_state || org.state_tag || null,
            })) || [];
        const { offer_states: _, offer_organizations: __, ...rest } = offer;
        return {
          ...rest,
          _offer_states: statesList,
          _redemption_organizations: redemptionOrganizations,
        } as OfferOption;
      });
    },
    enabled: isOpen,
  });

  // Filter offers by selected state
  const filteredOffers = useMemo(() => {
    if (!selectedState) {
      return [];
    }
    if (selectedState === "ALL") {
      return activeOffers;
    }
    return activeOffers.filter((offer) => {
      if (offer.geographic_scope === "NATIONAL") {
        return true;
      }
      if (offer.geographic_scope === "MULTI_STATE") {
        if (offer._offer_states && offer._offer_states.length > 0) {
          return offer._offer_states.includes(selectedState);
        }
        return true;
      }
      if (offer.geographic_scope === "SINGLE_STATE") {
        if (offer._offer_states && offer._offer_states.length > 0) {
          return offer._offer_states.includes(selectedState);
        }
        return false;
      }
      // No geographic scope set — exclude
      return false;
    });
  }, [activeOffers, selectedState]);

  // Auto-select "Custom Reward" if no active offers
  const effectiveRewardType =
    rewardType === "offer" && activeOffers.length === 0 ? "custom" : rewardType;

  // Fetch organizations for autocomplete (custom mode)
  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations-autocomplete", orgSearchTerm],
    queryFn: async () => {
      const query = supabase
        .from("organizations")
        .select("id, name, facility_id, city_tag, state_tag")
        .eq("is_archived", false)
        .not("facility_id", "is", null)
        .order("name", { ascending: true })
        .limit(20);

      if (orgSearchTerm.trim()) {
        query.or(
          `name.ilike.*${orgSearchTerm}*,city_tag.ilike.*${orgSearchTerm}*,state_tag.ilike.*${orgSearchTerm}*`,
        );
      }

      const { data, error } = await query;
      if (error) {
        throw error;
      }
      return data as Organization[];
    },
    enabled: isOpen && effectiveRewardType === "custom",
    staleTime: 30000,
  });

  // Fetch locations for selected organization (custom mode)
  const { data: locationsData } = useQuery({
    queryKey: ["organization-locations", formData.organizationId],
    queryFn: async () => {
      if (!formData.organizationId) {
        return null;
      }
      return getLocations(formData.organizationId, { limit: 100 });
    },
    enabled: !!formData.organizationId && effectiveRewardType === "custom",
  });

  // Fetch voucher CTAs for selected organization (custom mode)
  useQuery({
    queryKey: ["org-voucher-ctas", formData.organizationId],
    queryFn: async () => {
      if (!formData.organizationId) {
        setOrgVoucherCtas([]);
        setOrgVoucherCta(null);
        setSelectedOrgVoucherCtaId("");
        setCtaValidationErrors([]);
        return [];
      }
      const { data, error: ctaError } = await supabase
        .from("organization_ctas")
        .select("*")
        .eq("organization_id", formData.organizationId)
        .eq("reward_type", "voucher")
        .order("name", { ascending: true });

      if (ctaError || !data || data.length === 0) {
        setOrgVoucherCtas([]);
        setOrgVoucherCta(null);
        setSelectedOrgVoucherCtaId("");
        setCtaValidationErrors([]);
        return [];
      }

      const ctas = data as OrganizationCTA[];
      setOrgVoucherCtas(ctas);
      const current =
        ctas.find((cta) => cta.id === selectedOrgVoucherCtaId) || ctas[0];
      if (!selectedOrgVoucherCtaId || current.id !== selectedOrgVoucherCtaId) {
        setSelectedOrgVoucherCtaId(current.id);
      }
      const cta = current;
      setOrgVoucherCta(cta);

      // Validate CTA setup
      const errors: string[] = [];
      if (!cta.is_active) {
        errors.push("CTA is inactive — activate it before issuing vouchers.");
      }
      if (!cta.voucher_amount || cta.voucher_amount <= 0) {
        errors.push("CTA has no voucher amount configured.");
      }
      if (cta.voucher_end_date && new Date(cta.voucher_end_date) < new Date()) {
        errors.push("CTA voucher end date has passed.");
      }
      setCtaValidationErrors(errors);

      return ctas;
    },
    enabled: !!formData.organizationId && effectiveRewardType === "custom",
  });

  useEffect(() => {
    if (orgVoucherCtas.length === 0) {
      setOrgVoucherCta(null);
      setCtaValidationErrors([]);
      return;
    }
    const cta =
      orgVoucherCtas.find(
        (candidate) => candidate.id === selectedOrgVoucherCtaId,
      ) || orgVoucherCtas[0];
    if (!selectedOrgVoucherCtaId) {
      setSelectedOrgVoucherCtaId(cta.id);
    }
    setOrgVoucherCta(cta);
    const errors: string[] = [];
    if (!cta.is_active) {
      errors.push("CTA is inactive — activate it before issuing vouchers.");
    }
    if (!cta.voucher_amount || cta.voucher_amount <= 0) {
      errors.push("CTA has no voucher amount configured.");
    }
    if (cta.voucher_end_date && new Date(cta.voucher_end_date) < new Date()) {
      errors.push("CTA voucher end date has passed.");
    }
    setCtaValidationErrors(errors);
  }, [orgVoucherCtas, selectedOrgVoucherCtaId]);

  // Filter locations to only those with facility_ids
  const locationsWithFacilityIds = (locationsData?.data || []).filter(
    (loc) => loc.facility_id,
  );

  // Location is optional - only show dropdown if there are locations with facility IDs
  const showLocationDropdown =
    selectedOrg && locationsWithFacilityIds.length > 0;

  // Create voucher mutation
  const createVoucherMutation = useMutation({
    mutationFn: async () => {
      // Validate common fields
      if (!formData.email.trim()) {
        throw new Error("Email is required");
      }
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        throw new Error("First and last name are required");
      }
      if (!formData.issueReason.trim()) {
        throw new Error("Reason for issuing is required");
      }

      // Get current session for auth
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Not authenticated");
      }

      let voucherCode: string;
      let qrImageUrl: string | undefined;
      let isDuplicate = false;
      // Server-authoritative replay state. `idempotentReplay` comes from the hardened
      // generate-offer path; `isDuplicate` is the older offer-path flag. Either means we FOUND a
      // voucher rather than creating one.
      let alreadyRedeemed = false;
      let persistedValue: number | null = null;

      if (effectiveRewardType === "offer") {
        // ===== OFFER MODE =====
        if (!selectedOffer) {
          throw new Error("Please select an offer");
        }

        const requestBody = {
          offerId: selectedOffer.id,
          email: formData.email.trim().toLowerCase(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          membershipId: formData.membershipId.trim() || undefined,
          // Pass emailTemplate so generate-offer handles delivery with activity logging
          ...(formData.sendEmail
            ? { emailTemplate: "d-183cc17376894c7598004378a30ec91d" }
            : {}),
        };

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-offer`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(requestBody),
          },
        );

        const data = await response.json();

        if (!response.ok || !data.status) {
          throw new Error(data.message || "Failed to generate voucher");
        }

        voucherCode = data.data?.voucherCode;
        qrImageUrl = data.data?.qrImageUrl;
        isDuplicate =
          data.data?.isDuplicate || data.data?.idempotentReplay || false;
        alreadyRedeemed = data.data?.alreadyRedeemed || false;
        persistedValue =
          typeof data.data?.discountValue === "number"
            ? data.data.discountValue
            : null;

        if (!voucherCode) {
          throw new Error("Voucher was created but code was not returned");
        }
      } else {
        // ===== CUSTOM MODE =====
        if (!formData.organizationId) {
          throw new Error("Organization is required");
        }

        // If a voucher CTA exists, validate and use it
        if (orgVoucherCta) {
          if (ctaValidationErrors.length > 0) {
            throw new Error(
              `CTA setup issues: ${ctaValidationErrors.join(" ")}`,
            );
          }

          const requestBody = {
            ctaId: orgVoucherCta.id,
            email: formData.email.trim().toLowerCase(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            membershipId: formData.membershipId.trim() || undefined,
          };

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-offer`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify(requestBody),
            },
          );

          const data = await response.json();

          if (!response.ok || !data.status) {
            throw new Error(data.message || "Failed to generate voucher");
          }

          voucherCode = data.data?.voucherCode;
          qrImageUrl = data.data?.qrImageUrl;
          isDuplicate = isDuplicate || data.data?.idempotentReplay || false;
          alreadyRedeemed =
            alreadyRedeemed || data.data?.alreadyRedeemed || false;
          if (typeof data.data?.discountValue === "number") {
            persistedValue = data.data.discountValue;
          }

          if (!voucherCode) {
            throw new Error("Voucher was created but code was not returned");
          }
        } else {
          // No voucher CTA — use manual voucher parameters.
          // Retailer voucher value: whole dollars only, $1–$100 (default $20).
          const parsedAmount = Number(formData.voucherAmount);
          if (
            !formData.voucherAmount ||
            !Number.isInteger(parsedAmount) ||
            parsedAmount < 1 ||
            parsedAmount > 100
          ) {
            throw new Error(
              "Voucher amount must be a whole dollar value between $1 and $100",
            );
          }

          // Determine facility_ids
          let facilityIds: string[] = [];
          if (formData.locationId && locationsWithFacilityIds.length > 0) {
            const selectedLocation = locationsWithFacilityIds.find(
              (l) => l.id === formData.locationId,
            );
            if (selectedLocation?.facility_id) {
              facilityIds = [selectedLocation.facility_id];
            }
          } else if (selectedOrg?.facility_id) {
            facilityIds = [selectedOrg.facility_id];
          }

          const requestBody = {
            email: formData.email.trim().toLowerCase(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            membershipId: formData.membershipId.trim() || undefined,
            voucherName: formData.voucherName.trim() || "Reward Voucher",
            discountValue: parsedAmount,
            facilityIds: facilityIds.length > 0 ? facilityIds : undefined,
            startDate: formData.startDate
              ? new Date(formData.startDate).getTime()
              : Date.now(),
            endDate: formData.endDate
              ? new Date(formData.endDate).getTime()
              : undefined,
          };

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-offer`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify(requestBody),
            },
          );

          const data = await response.json();

          if (!response.ok || !data.status) {
            throw new Error(data.message || "Failed to generate voucher");
          }

          voucherCode = data.data?.voucherCode;
          qrImageUrl = data.data?.qrImageUrl;
          isDuplicate = isDuplicate || data.data?.idempotentReplay || false;
          alreadyRedeemed =
            alreadyRedeemed || data.data?.alreadyRedeemed || false;
          if (typeof data.data?.discountValue === "number") {
            persistedValue = data.data.discountValue;
          }

          if (!voucherCode) {
            throw new Error("Voucher was created but code was not returned");
          }
        }
      }

      // Optionally send email for custom/retailer mode.
      // Offer mode email is handled server-side by generate-offer (emailTemplate param above).
      // Never email an already-spent voucher. generate-offer suppresses its own delivery email
      // on a replay, but this custom/retailer path sends independently and was not gated — so a
      // replay could still email a voucher that had already been redeemed.
      if (
        formData.sendEmail &&
        effectiveRewardType !== "offer" &&
        !alreadyRedeemed
      ) {
        try {
          // Prefer the PERSISTED voucher value. Building the amount from the form/CTA meant a
          // replay could advertise a different amount than the voucher actually carries.
          const emailVoucherAmount =
            persistedValue ??
            (orgVoucherCta
              ? orgVoucherCta.voucher_amount || 0
              : parseFloat(formData.voucherAmount));
          const emailVoucherName = orgVoucherCta
            ? orgVoucherCta.name
            : formData.voucherName.trim() || "Reward Voucher";

          let redeemableAt: string | undefined;
          if (formData.locationId && locationsWithFacilityIds.length > 0) {
            const selectedLocation = locationsWithFacilityIds.find(
              (l) => l.id === formData.locationId,
            );
            if (selectedLocation) {
              redeemableAt = selectedLocation.address_city
                ? `${selectedLocation.name} - ${selectedLocation.address_city}`
                : selectedLocation.name;
            }
          }
          if (!redeemableAt && selectedOrg) {
            redeemableAt = selectedOrg.name;
          }

          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                type: "voucher",
                to: formData.email.trim(),
                toName: `${formData.firstName} ${formData.lastName}`.trim(),
                voucherCode: voucherCode,
                voucherAmount: emailVoucherAmount,
                voucherName: emailVoucherName,
                eventName: redeemableAt,
                qrImageUrl: qrImageUrl,
                expirationDate: formData.endDate
                  ? new Date(formData.endDate).toLocaleDateString()
                  : undefined,
              }),
            },
          );
        } catch (emailError) {
          console.warn("Failed to send voucher email:", emailError);
        }
      }

      return {
        voucherCode,
        qrImageUrl,
        isDuplicate,
        alreadyRedeemed,
        persistedValue,
      };
    },
    onSuccess: (result) => {
      setCreatedVoucherCode(result.voucherCode);
      setIsDuplicateVoucher(result.isDuplicate);
      setAlreadyRedeemed(result.alreadyRedeemed);
      setPersistedAmount(result.persistedValue);
      setStep("success");
      queryClient.invalidateQueries({ queryKey: ["redemptions"] });
      onSuccess?.(result.voucherCode);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Handle organization selection
  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setOrgVoucherCtas([]);
    setOrgVoucherCta(null);
    setSelectedOrgVoucherCtaId("");
    setCtaValidationErrors([]);
    setFormData((prev) => ({
      ...prev,
      organizationId: org.id,
      locationId: "", // Reset location when org changes
    }));
    setOrgSearchTerm(org.name);
    setShowOrgDropdown(false);
  };

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    createVoucherMutation.mutate();
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      membershipId: "",
      organizationId: "",
      locationId: "",
      voucherAmount: "20",
      voucherName: "Reward Voucher",
      startDate: new Date().toISOString().split("T")[0],
      endDate: "",
      sendEmail: true,
      issueReason: "",
    });
    setRewardType("offer");
    setSelectedOffer(null);
    setSelectedState("");
    setOrgSearchTerm("");
    setSelectedOrg(null);
    setOrgVoucherCtas([]);
    setOrgVoucherCta(null);
    setSelectedOrgVoucherCtaId("");
    setCtaValidationErrors([]);
    setCreatedVoucherCode("");
    setIsDuplicateVoucher(false);
    setStep("form");
    setError("");
    onClose();
  };

  // Copy voucher code to clipboard
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(createdVoucherCode);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (!isOpen) {
    return null;
  }

  // Success screen display values
  let successAmount: number | null = null;
  if (persistedAmount !== null) {
    // Server-confirmed value of the voucher that actually exists — always wins over the form.
    successAmount = persistedAmount;
  } else if (effectiveRewardType === "offer" && selectedOffer) {
    successAmount = selectedOffer.value_amount;
  } else if (orgVoucherCta?.voucher_amount) {
    successAmount = orgVoucherCta.voucher_amount;
  } else if (formData.voucherAmount) {
    successAmount = parseFloat(formData.voucherAmount);
  }
  const successLabel =
    effectiveRewardType === "offer" && selectedOffer
      ? selectedOffer.name
      : selectedOrg?.name || "";

  const formatScopeLabel = (scope: string | null | undefined) => {
    if (scope === "ALL_ORGANIZATIONS") {
      return "All Organizations";
    }
    if (scope === "MULTIPLE_ORGANIZATIONS") {
      return "Multiple Organizations";
    }
    if (scope === "SINGLE_ORGANIZATION") {
      return "Single Organization";
    }
    return "Not specified";
  };

  const formatGeographicScope = (offer: OfferOption) => {
    if (offer.geographic_scope === "NATIONAL") {
      return "National";
    }
    const states = offer._offer_states || [];
    if (states.length > 0) {
      return states.join(", ");
    }
    if (offer.geographic_scope === "MULTI_STATE") {
      return "Multiple states";
    }
    if (offer.geographic_scope === "SINGLE_STATE") {
      return "Single state";
    }
    return "Not specified";
  };

  const formatRedemptionDetails = (offer: OfferOption) => {
    const orgs = offer._redemption_organizations || [];
    if (orgs.length > 0) {
      return orgs
        .map((org) => {
          const place = [org.city, org.state].filter(Boolean).join(", ");
          return place ? `${org.name} (${place})` : org.name;
        })
        .join("; ");
    }
    if (
      offer.redemption_scope === "ALL_ORGANIZATIONS" &&
      offer.allowed_organization_types?.length
    ) {
      return `Any ${offer.allowed_organization_types.join(", ")} organization`;
    }
    return formatScopeLabel(offer.redemption_scope);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-lg transform rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {step === "form" ? "Issue Reward" : "Voucher Created"}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {step === "form" ? (
            <form onSubmit={handleSubmit}>
              <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
                {/* Error message */}
                {error && (
                  <div className="rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Reward Type Toggle */}
                <div>
                  <div className="flex rounded-lg border border-gray-300 p-1 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setRewardType("offer")}
                      className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                        effectiveRewardType === "offer"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      } ${activeOffers.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={activeOffers.length === 0}
                    >
                      Offer Reward
                    </button>
                    <button
                      type="button"
                      onClick={() => setRewardType("custom")}
                      className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                        effectiveRewardType === "custom"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Retailer Voucher
                    </button>
                  </div>
                  {activeOffers.length === 0 && (
                    <p className="mt-1.5 text-xs text-amber-600">
                      No active offers available. Use Retailer Voucher to issue
                      a voucher manually.
                    </p>
                  )}
                </div>

                {/* Offer Selection (offer mode) */}
                {effectiveRewardType === "offer" && (
                  <>
                    {/* State Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <select
                        value={selectedState}
                        onChange={(e) => {
                          setSelectedState(e.target.value);
                          setSelectedOffer(null); // Reset offer when state changes
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="">Select a state...</option>
                        <option value="ALL">All States</option>
                        {US_STATES.map((st) => (
                          <option key={st.code} value={st.code}>
                            {st.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Offer Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Offer *
                      </label>
                      <select
                        value={selectedOffer?.id || ""}
                        onChange={(e) => {
                          const offer = filteredOffers.find(
                            (o) => o.id === e.target.value,
                          );
                          setSelectedOffer(offer || null);
                        }}
                        className={`w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${!selectedState ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                        required
                        disabled={!selectedState}
                      >
                        <option value="">
                          {selectedState
                            ? "Choose an offer..."
                            : "Select a state first"}
                        </option>
                        {filteredOffers.map((offer) => (
                          <option key={offer.id} value={offer.id}>
                            {offer.name}
                            {offer.value_amount !== null
                              ? ` — $${offer.value_amount}`
                              : ""}
                          </option>
                        ))}
                      </select>
                      {selectedState &&
                        selectedState !== "ALL" &&
                        filteredOffers.length === 0 && (
                          <p className="mt-1.5 text-xs text-amber-600">
                            No offers available for this state.
                          </p>
                        )}
                    </div>

                    {/* Offer Preview Card */}
                    {selectedOffer && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            Amount
                          </span>
                          <span className="text-sm text-gray-900">
                            {selectedOffer.value_amount !== null
                              ? `$${selectedOffer.value_amount.toFixed(2)}`
                              : "N/A"}
                            {selectedOffer.value_type === "percentage"
                              ? " (%)"
                              : ""}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            Scope Type
                          </span>
                          <span className="text-sm text-gray-900">
                            {formatScopeLabel(selectedOffer.redemption_scope)}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm font-medium text-gray-700">
                            Redeemable At
                          </span>
                          <span className="max-w-[60%] text-right text-sm text-gray-900">
                            {formatRedemptionDetails(selectedOffer)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            Available In
                          </span>
                          <span className="text-sm text-gray-900">
                            {formatGeographicScope(selectedOffer)}
                          </span>
                        </div>
                        {(selectedOffer.valid_from ||
                          selectedOffer.valid_to) && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">
                              Valid
                            </span>
                            <span className="text-sm text-gray-900">
                              {selectedOffer.valid_from
                                ? formatOfferValidityDate(
                                    selectedOffer.valid_from,
                                  )
                                : "Now"}
                              {" — "}
                              {selectedOffer.valid_to
                                ? formatOfferValidityDate(
                                    selectedOffer.valid_to,
                                  )
                                : "No expiration"}
                            </span>
                          </div>
                        )}
                        {selectedOffer.voucher_source_description && (
                          <div className="flex items-start justify-between">
                            <span className="text-sm font-medium text-gray-700">
                              Description
                            </span>
                            <span className="text-sm text-gray-900 text-right max-w-[60%]">
                              {selectedOffer.voucher_source_description}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Recipient Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    required
                  />
                </div>

                {/* Member Number (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Member Number{" "}
                    <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="membershipId"
                    value={formData.membershipId}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Custom mode fields: Org, Location, Amount, Name, Dates */}
                {effectiveRewardType === "custom" && (
                  <>
                    {/* Organization Autocomplete */}
                    <div className="relative" ref={orgDropdownRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={orgSearchTerm}
                          onChange={(e) => {
                            setOrgSearchTerm(e.target.value);
                            setShowOrgDropdown(true);
                            if (
                              selectedOrg &&
                              e.target.value !== selectedOrg.name
                            ) {
                              setSelectedOrg(null);
                              setOrgVoucherCtas([]);
                              setOrgVoucherCta(null);
                              setSelectedOrgVoucherCtaId("");
                              setCtaValidationErrors([]);
                              setFormData((prev) => ({
                                ...prev,
                                organizationId: "",
                                locationId: "",
                              }));
                            }
                          }}
                          onKeyDown={preventEnterSubmit}
                          onFocus={() => setShowOrgDropdown(true)}
                          placeholder="Search organizations..."
                          className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <MagnifyingGlassIcon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      </div>

                      {/* Organization dropdown */}
                      {showOrgDropdown && organizations.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto">
                          {organizations.map((org) => (
                            <button
                              key={org.id}
                              type="button"
                              onClick={() => handleSelectOrg(org)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                            >
                              <div className="font-medium text-gray-900">
                                {org.name}
                              </div>
                              {(org.city_tag || org.state_tag) && (
                                <div className="text-sm text-gray-500">
                                  {[org.city_tag, org.state_tag]
                                    .filter(Boolean)
                                    .join(", ")}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Selected org indicator */}
                      {selectedOrg && (
                        <div className="mt-1 text-sm text-green-600">
                          Selected: {selectedOrg.name}
                        </div>
                      )}
                    </div>

                    {/* CTA Selection + Validation Status */}
                    {selectedOrg && orgVoucherCta && (
                      <div
                        className={`rounded-lg border p-3 space-y-1.5 ${
                          ctaValidationErrors.length > 0
                            ? "border-amber-300 bg-amber-50"
                            : "border-green-300 bg-green-50"
                        }`}
                      >
                        {orgVoucherCtas.length > 1 && (
                          <div className="mb-2">
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Voucher CTA
                            </label>
                            <select
                              value={selectedOrgVoucherCtaId}
                              onChange={(e) =>
                                setSelectedOrgVoucherCtaId(e.target.value)
                              }
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            >
                              {orgVoucherCtas.map((cta) => (
                                <option key={cta.id} value={cta.id}>
                                  {cta.name}
                                  {cta.voucher_amount
                                    ? ` — $${cta.voucher_amount.toFixed(2)}`
                                    : ""}
                                  {!cta.is_active ? " (inactive)" : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          {ctaValidationErrors.length > 0 ? (
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                          ) : (
                            <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium text-gray-900">
                            Voucher CTA: {orgVoucherCta.name}
                          </span>
                        </div>
                        {ctaValidationErrors.length > 0 ? (
                          <ul className="ml-7 space-y-0.5">
                            {ctaValidationErrors.map((err, i) => (
                              <li key={i} className="text-sm text-amber-700">
                                {err}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="ml-7 text-sm text-green-700">
                            ${orgVoucherCta.voucher_amount?.toFixed(2)} voucher
                            {orgVoucherCta.voucher_expiry_days
                              ? ` — expires ${orgVoucherCta.voucher_expiry_days} days after issue`
                              : ""}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedOrg && !orgVoucherCta && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-sm text-gray-500">
                          No voucher CTA configured for this organization.
                          Voucher will be created with manual settings below.
                        </p>
                      </div>
                    )}

                    {/* Location Selection (shown when org has locations with facility IDs) */}
                    {showLocationDropdown && !orgVoucherCta && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location{" "}
                          <span className="text-gray-400">(optional)</span>
                        </label>
                        <select
                          name="locationId"
                          value={formData.locationId}
                          onChange={handleInputChange}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="">
                            All locations (organization level)
                          </option>
                          {locationsWithFacilityIds.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                              {loc.address_city && ` - ${loc.address_city}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Manual voucher fields — only shown when no voucher CTA exists */}
                    {!orgVoucherCta && (
                      <>
                        {/* Voucher Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Voucher Amount *
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                              $
                            </span>
                            <input
                              type="number"
                              name="voucherAmount"
                              value={formData.voucherAmount}
                              onChange={(e) => {
                                // Whole dollars only, $1–$100. Strip non-digits
                                // and cap at 100; allow empty while editing.
                                const digits = e.target.value.replace(
                                  /[^0-9]/g,
                                  "",
                                );
                                let next = digits;
                                if (digits !== "") {
                                  next = String(
                                    Math.min(parseInt(digits, 10), 100),
                                  );
                                }
                                setFormData((prev) => ({
                                  ...prev,
                                  voucherAmount: next,
                                }));
                                setError("");
                              }}
                              min="1"
                              max="100"
                              step="1"
                              inputMode="numeric"
                              placeholder="20"
                              className="w-full rounded-md border border-gray-300 px-3 py-2 pl-7 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              required
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Whole dollar amount between $1 and $100. Defaults to
                            $20.
                          </p>
                        </div>

                        {/* Voucher Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Voucher Name{" "}
                            <span className="text-gray-400">(optional)</span>
                          </label>
                          <input
                            type="text"
                            name="voucherName"
                            value={formData.voucherName}
                            onChange={handleInputChange}
                            placeholder="Reward Voucher"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Date
                            </label>
                            <input
                              type="date"
                              name="startDate"
                              value={formData.startDate}
                              onChange={handleInputChange}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Date{" "}
                              <span className="text-gray-400">(optional)</span>
                            </label>
                            <input
                              type="date"
                              name="endDate"
                              value={formData.endDate}
                              onChange={handleInputChange}
                              min={formData.startDate}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Reason for issuing */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Issuing *
                  </label>
                  <textarea
                    name="issueReason"
                    value={formData.issueReason}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Notes about why this reward is being issued..."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    required
                  />
                </div>

                {/* Send Email Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    name="sendEmail"
                    checked={formData.sendEmail}
                    onChange={handleInputChange}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor="sendEmail"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Email voucher to recipient
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createVoucherMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createVoucherMutation.isPending
                    ? "Creating..."
                    : "Issue Reward"}
                </button>
              </div>
            </form>
          ) : (
            /* Success Step */
            <div className="px-6 py-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {alreadyRedeemed
                  ? "Voucher Already Redeemed"
                  : isDuplicateVoucher
                    ? "Voucher Already Issued"
                    : "Voucher Created Successfully!"}
              </h3>
              {alreadyRedeemed ? (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 mb-3">
                  <p className="text-sm text-red-800">
                    This offer was already issued to {formData.email} and that
                    voucher has already been redeemed. No new voucher was
                    created and nothing was emailed. Escalate for review rather
                    than issuing another voucher.
                  </p>
                </div>
              ) : isDuplicateVoucher ? (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 mb-3">
                  <p className="text-sm text-amber-800">
                    This offer has already been issued to {formData.email}. The
                    existing voucher code is shown below.
                  </p>
                </div>
              ) : null}
              <p className="text-sm text-gray-600 mb-4">
                {alreadyRedeemed
                  ? "No new voucher was created and no email was sent."
                  : isDuplicateVoucher
                    ? formData.sendEmail
                      ? `The existing voucher was re-sent to ${formData.email}.`
                      : "No new voucher was created."
                    : formData.sendEmail
                      ? `The voucher has been created and emailed to ${formData.email}`
                      : "The voucher has been created."}
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-1">Voucher Code</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-2xl font-bold text-primary-600 tracking-wider">
                    {createdVoucherCode}
                  </span>
                  <button
                    onClick={handleCopyCode}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="Copy code"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {successAmount !== null
                    ? `$${Number(successAmount).toFixed(2)}`
                    : ""}
                  {successLabel ? ` — ${successLabel}` : ""}
                </p>
              </div>

              <button
                onClick={() => {
                  onSuccessDismiss?.();
                  handleClose();
                }}
                className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
